import React from 'react';
import theme from './Tags.module.scss';
import { ProjectTagExtended } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faTag, faTimes } from '@fortawesome/pro-light-svg-icons';
import { classnames } from '@/utils/html';
import { Tooltip } from '@/components/Tooltip';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button } from '@/components/Buttons';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { Toast, Sortable, ToggleInput, arrayMove } from '@ygtechnologies/bygr-frontend-component-library';

interface EditTagsProps {
    tags: ProjectTagExtended[];
    onChange(newValue: ProjectTagExtended[]): void;
    // onTagChange: (change: BatchedChanges<ProjectTagExtended>) => void;
    // onTagDelete: (id: number) => void;
}
const EditTags: React.FC<EditTagsProps> = ({ tags, onChange }) => {
    const onSortEnd = ({ oldIndex, newIndex }) => {
        const newArray = arrayMove(tags, oldIndex, newIndex);
        triggerHandler(onChange, newArray);
    };

    const handleTagChange = (tag: ProjectTagExtended, changes: Partial<ProjectTagExtended>) => {
        // No empty and no used names.
        const newLabel = changes.label;

        if (!newLabel) {
            Toast.error(<Trans>Tag name can not be blank.</Trans>);
            return;
        }

        if (tags!.some((t) => t.id !== tag.id && t.label.toLowerCase() === newLabel.toLowerCase())) {
            Toast.error(<Trans>A tag with this name already exists</Trans>);
            return;
        }

        onChange(arrayReplace(tags, tag, { ...tag, ...changes }));
    };

    const handleTagDelete = (id: number) => {
        onChange(tags!.filter((t) => t.id !== id));
    };

    return (
        <Sortable items={tags || []} idField={(x) => x.id} onSortEnd={onSortEnd} direction="both" className={theme.pillList}>
            {(tag, { handleProps, isDragging }) => (
                <Tooltip
                    content={
                        <>
                            <Trans>Units</Trans>: {tag.residentialUnitTagCount}
                            <br />
                            <Trans>Options</Trans>: {tag.linkGroupTagCount}
                            <br />
                            <Trans>Orders</Trans>: {tag.orderTagCount}
                            <br />
                            <Trans>Change requests</Trans>: {tag.changeRequestTagCount}
                            <br />
                            <Trans>External agreements</Trans>: {tag.externalAgreementCount}
                            <br />
                            <Trans>Aftermarket</Trans>: {tag.aftermarketCount}
                            {!tag.canBeDeleted && (
                                <>
                                    <br />
                                    <br />

                                    <Trans>Object cannot be deleted while it is in use</Trans>
                                </>
                            )}
                            {!tag.canBeRenamed && (
                                <>
                                    <br />
                                    <br />

                                    <Trans>
                                        This tag cannot be renamed because it is associated with an existing order.
                                    </Trans>
                                </>
                            )}
                        </>
                    }>
                    <li className={theme.pillListItem}>
                        <div {...handleProps} className={classnames(theme.tagButton, theme.dragHandle)}>
                            <FontAwesomeIcon icon={faGripLines} size="lg" />
                        </div>
                        <FontAwesomeIcon className={theme.icon} icon={faTag} />
                        <div
                            className={theme.text}
                            onClick={() => {
                                Toast.warn(
                                    t`This tag cannot be renamed because it is associated with an existing order.`
                                );
                            }}>
                            <ToggleInput
                                disabled={!tag.canBeRenamed}
                                iconMode="over"
                                onAccept={(e) => {
                                    const trimmedInput = e.target.value.trim();
                                    if (trimmedInput === tag.label) {
                                        return;
                                    }
                                    return handleTagChange(tag, { label: trimmedInput });
                                }}
                                value={tag.label}
                            />
                        </div>
                        <Button
                            disabled={!tag.canBeDeleted}
                            onClick={() => handleTagDelete(tag.id)}
                            icon={faTimes}
                            className={classnames(theme.tagButton, theme.deleteButton)}
                        />
                    </li>
                </Tooltip>
            )}
        </Sortable>
    );
};

export default EditTags;
